/**
 * @tt/lobby/cosmetic-resolver — Phase F-Foundation.
 *
 * Bridges the catalog id-space (e.g. `body-8`, `accent-50`, `ember-ring`)
 * and the inventory storage shape (numeric index for body/hat/face/accent;
 * semantic string id for palette/border/background/particles/title).
 *
 * Used by:
 *   - lambdas/season + lambdas/shop on claim/purchase: take a catalog id
 *     (from the season reward def or shop catalog), resolve the kind and
 *     the value to write, and append it to `inventory.<bucket>`.
 *   - LockerLayout (F1/F3/F4): walk an owned inventory bucket and look
 *     up the rendering catalog id with `inventoryToCatalogId`.
 *
 * Convention (defined in docs/phase-f-fixes.md):
 *   body / hat / face   → catalog id `${kind}-${arrayIndex}`, inventory
 *                         stores the numeric index (matches BODY_SHAPES /
 *                         HAT_SHAPES / FACE_SHAPES).
 *   accent              → catalog id `accent-${hue}`, inventory stores the
 *                         numeric hue value (matches HUES).
 *   palette / border /
 *   background /
 *   particles / title   → catalog id is the semantic registry id
 *                         (`palette-dusk`, `ember-ring`, `tavern-hearth`,
 *                         `Tavern Regular`, …); inventory stores the
 *                         string verbatim.
 */
import { COSMETIC_CATALOG, type CosmeticKind } from './cosmetics-catalog';

/** What gets stored in inventory for a given cosmetic kind. */
export type InventoryValue = number | string;

const NUMERIC_RE = /^(?:body|hat|face)-(-?\d+)$/;
const ACCENT_RE = /^accent-(-?\d+)$/;
// Phase F5 — seat catalog ids. Border / background / particle store the
// suffix as a string (matches SEAT_BORDERS[*].id etc. — semantic ids).
// Bg-hues store the suffix as a number (matches SEAT_BG_HUES[*].id).
const SEAT_BORDER_RE = /^seat-border-(.+)$/;
const SEAT_BACKGROUND_RE = /^seat-background-(.+)$/;
const SEAT_PARTICLE_RE = /^seat-particle-(.+)$/;
const SEAT_BGHUE_RE = /^seat-bghue-(-?\d+)$/;
// Phase F5 — masks. Catalog id `mask-${semanticId}`; inventory bucket
// `masks` stores the semantic suffix verbatim.
const MASK_RE = /^mask-(.+)$/;

/** Resolve a catalog id into the (kind, value) that gets stored in
 *  `inventory.<bucket>`. Returns null if the id isn't in the catalog. */
export function resolveCosmeticToInventory(
  catalogId: string,
): { kind: CosmeticKind; value: InventoryValue } | null {
  const def = COSMETIC_CATALOG.find((c) => c.id === catalogId);
  if (!def) return null;
  switch (def.kind) {
    case 'body':
    case 'hat':
    case 'face': {
      const m = NUMERIC_RE.exec(catalogId);
      if (!m) return null;
      return { kind: def.kind, value: Number(m[1]) };
    }
    case 'accent': {
      const m = ACCENT_RE.exec(catalogId);
      if (!m) return null;
      return { kind: 'accent', value: Number(m[1]) };
    }
    case 'palette':
    case 'border':
    case 'background':
    case 'particles':
    case 'title':
      // Semantic ids — inventory stores the string verbatim.
      return { kind: def.kind, value: catalogId };
    case 'seat-border': {
      const m = SEAT_BORDER_RE.exec(catalogId);
      if (!m) return null;
      return { kind: 'seat-border', value: m[1]! };
    }
    case 'seat-background': {
      const m = SEAT_BACKGROUND_RE.exec(catalogId);
      if (!m) return null;
      return { kind: 'seat-background', value: m[1]! };
    }
    case 'seat-particle': {
      const m = SEAT_PARTICLE_RE.exec(catalogId);
      if (!m) return null;
      return { kind: 'seat-particle', value: m[1]! };
    }
    case 'seat-bghue': {
      const m = SEAT_BGHUE_RE.exec(catalogId);
      if (!m) return null;
      return { kind: 'seat-bghue', value: Number(m[1]) };
    }
    case 'mask': {
      const m = MASK_RE.exec(catalogId);
      if (!m) return null;
      return { kind: 'mask', value: m[1]! };
    }
  }
}

/** Inverse: given an inventory bucket (kind) + value, return the catalog
 *  id for rendering / lookup. Used by the locker to walk an owned
 *  inventory and show CosmeticPreviews. Returns the predicted id even if
 *  no catalog entry exists for that value (so legacy inventory data
 *  still renders something), so callers should additionally check
 *  `getCosmetic(id)` if they need the def. */
export function inventoryToCatalogId(kind: CosmeticKind, value: InventoryValue): string | null {
  switch (kind) {
    case 'body':
    case 'hat':
    case 'face':
      return `${kind}-${value}`;
    case 'accent':
      return `accent-${value}`;
    case 'palette':
    case 'border':
    case 'background':
    case 'particles':
    case 'title':
      return String(value);
    case 'seat-border':
      return `seat-border-${value}`;
    case 'seat-background':
      return `seat-background-${value}`;
    case 'seat-particle':
      return `seat-particle-${value}`;
    case 'seat-bghue':
      return `seat-bghue-${value}`;
    case 'mask':
      return `mask-${value}`;
  }
}

/** Map cosmetic kind → inventory bucket name. Mirrors the storage shape
 *  in DEFAULT_INVENTORY (lambdas/characters/src/index.ts). Used by both
 *  the season-claim handler and the shop-purchase handler when pushing
 *  newly granted items into the right bucket. */
export function inventoryBucketFor(kind: CosmeticKind): string {
  switch (kind) {
    case 'body':            return 'bodies';
    case 'hat':             return 'hats';
    case 'face':            return 'faces';
    case 'accent':          return 'accentHues';
    case 'palette':         return 'palettes';
    case 'border':          return 'borders';
    case 'background':      return 'backgrounds';
    case 'particles':       return 'seatParticles';
    case 'title':           return 'titles';
    // Phase F5 — seat-tile cosmetics. Distinct buckets from the avatar
    // border/background/particles above; same registry shape as SEAT_*
    // in `./presence.ts`.
    case 'seat-border':     return 'seatBorders';
    case 'seat-background': return 'seatBackgrounds';
    case 'seat-particle':   return 'seatParticles';
    case 'seat-bghue':      return 'seatBgHues';
    case 'mask':            return 'masks';
  }
}
